import React, { useEffect, useState } from "react";
import { api, formatCurrency } from "@/lib/api";
import { Home, CalendarClock, TriangleAlert, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export function MortgageCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/analytics/mortgage").then((r) => setData(r.data)).catch(() => setData({ available: false }));
  }, []);

  if (!data || !data.available) return null;

  const {
    creditor, currency, remaining_amount, original_amount, paid_amount, progress_pct,
    monthly_payment, months_paid, term_months, months_remaining, next_payment_date, rate_type,
  } = data;

  const nextDateStr = next_payment_date ? new Date(next_payment_date).toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" }) : "—";

  return (
    <div className="card-surface p-6" data-testid="mortgage-card">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-[#0F291E]/10 flex items-center justify-center">
          <Home className="w-4 h-4 text-[#0F291E]" />
        </div>
        <div className="flex-1">
          <p className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">Hipoteca</p>
          <h3 className="font-display text-lg font-semibold text-[#0A110E]">{creditor}</h3>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-[#8A9992] bg-[#EBEBEA]/60 px-2 py-1 rounded-full">
          {rate_type === "variable" ? "Variable" : "Fija"}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="rounded-xl border border-[#EBEBEA] px-3 py-2">
          <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">Cuota mensual</div>
          <div className="font-display text-lg font-semibold tabular-nums text-[#0A110E]">{formatCurrency(monthly_payment, currency)}</div>
        </div>
        <div className="rounded-xl border border-[#EBEBEA] px-3 py-2">
          <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992] flex items-center gap-1">
            <CalendarClock className="w-3 h-3" /> Próximo pago
          </div>
          <div className="text-sm font-semibold tabular-nums text-[#0A110E] mt-1">{nextDateStr}</div>
        </div>
      </div>

      <div className="mb-1 flex items-center justify-between text-xs text-[#5F7368] tabular-nums">
        <span>Pagado {formatCurrency(paid_amount, currency)}</span>
        <span>Restante {formatCurrency(remaining_amount, currency)}</span>
      </div>
      <div className="w-full h-2 rounded-full bg-[#EBEBEA] overflow-hidden">
        <div
          className="h-full bg-[#1C7C54] transition-all duration-500"
          style={{ width: `${Math.min(100, progress_pct || 0)}%` }}
        />
      </div>
      <div className="mt-2 flex items-center justify-between text-xs">
        <span className="text-[#8A9992] tabular-nums">
          {months_paid} de {term_months} cuotas · {months_remaining} restantes
        </span>
        <span className="font-semibold text-[#1C7C54] tabular-nums">{progress_pct}%</span>
      </div>
    </div>
  );
}

export function RecurringAlertBanner() {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const currency = user?.base_currency || "USD";

  useEffect(() => {
    api.get("/analytics/recurring")
      .then((r) => setAlerts((r.data || []).filter((x) => x.delta_pct > 10)))
      .catch(() => setAlerts([]));
  }, []);

  if (!alerts || alerts.length === 0 || dismissed) return null;

  return (
    <div className="fade-up rounded-2xl border border-[#D95D39]/30 bg-[#D95D39]/5 p-5" data-testid="recurring-alert">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#D95D39]/15 flex items-center justify-center flex-shrink-0">
          <TriangleAlert className="w-4 h-4 text-[#D95D39]" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-[#0A110E]">
              {alerts.length === 1 ? "1 suscripción se ha encarecido" : `${alerts.length} cargos recurrentes al alza`}
            </p>
            <button
              onClick={() => setDismissed(true)}
              data-testid="dismiss-recurring-alert"
              className="p-1 rounded hover:bg-[#D95D39]/10"
            >
              <X className="w-4 h-4 text-[#8A9992]" />
            </button>
          </div>
          <ul className="mt-2 space-y-1">
            {alerts.slice(0, 5).map((a) => (
              <li key={a.key} className="text-sm text-[#5F7368] flex items-center gap-2">
                <span className="font-medium text-[#0A110E]">{a.example_description}</span>
                <span className="tabular-nums">{formatCurrency(a.last_amount, currency)}</span>
                <span className="text-[#D95D39] font-semibold tabular-nums">+{a.delta_pct}% vs media</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
